import type { ScoreBreakdown, ScoreMethod, UnavailableReason } from '@vizit/api';

type FormulaComponentKey = keyof ScoreBreakdown['components'];

/**
 * How a PDP's score renders:
 * - `gam`: today's GAM scorecard, unchanged.
 * - `formula`: the served formula scorecard; `absentComponents` render the absent-content state.
 * - `unavailable`: the score is withheld; `reason` is only present for internal users (the BE strips it).
 */
type FormulaScoreState =
  | { kind: 'gam' }
  | { kind: 'formula'; absentComponents: FormulaComponentKey[] }
  | { kind: 'unavailable'; reason: UnavailableReason | null };

/**
 * Resolves the score state from `score_method` and the breakdown `status` — never from the
 * LaunchDarkly flag, so a formula-served PDP can't silently fall back to a GAM number.
 */
function resolveFormulaScoreState(params: {
  scoreMethod: ScoreMethod | null | undefined;
  breakdown: ScoreBreakdown | null | undefined;
}): FormulaScoreState {
  const { scoreMethod, breakdown } = params;
  // A shadow org serves GAM even when internal users see an UNAVAILABLE breakdown: its number is real.
  if (scoreMethod !== 'FORMULA') return { kind: 'gam' };
  if (breakdown == null) return { kind: 'unavailable', reason: null };
  if (breakdown.status === 'UNAVAILABLE') return { kind: 'unavailable', reason: breakdown.unavailable_reason ?? null };

  const entries = Object.entries(breakdown.components) as [FormulaComponentKey, { tag: string | null }][];
  // A served breakdown never carries DATA_FAILURE per the contract; if one leaks through,
  // withholding is the only safe render (there is no per-card failure state).
  if (entries.some(([, component]) => component.tag === 'DATA_FAILURE')) return { kind: 'unavailable', reason: null };
  return {
    kind: 'formula',
    absentComponents: entries.filter(([, component]) => component.tag === 'ABSENT_CONTENT').map(([key]) => key),
  };
}

// A missing score in these statuses isn't withheld: (re)scoring shows Processing, and a missing
// category shows its fixable Error, so neither may read as "unavailable, nothing to change".
const NOT_WITHHELD_SCORE_STATUSES = new Set(['CREATED', 'PROCESSING', 'RESCORE_PENDING', 'CATEGORY_NOT_FOUND']);

/** Whether a missing PDP score in this score status can mean withheld. */
function canScoreBeWithheld(scoreStatus: string | null | undefined): boolean {
  return scoreStatus == null || !NOT_WITHHELD_SCORE_STATUSES.has(scoreStatus);
}

// The statuses where a list row would otherwise show its score. Anything else (in flight, erroring,
// missing or unrecognized) keeps the row's own Processing, Error or N/A marker.
const WITHHOLDABLE_LISTING_STATUSES = new Set(['SCORED', 'REFRESH_PENDING']);

/**
 * A list row's score is withheld when it's formula-served, has no listing score, and is in a status
 * that would otherwise show a score. Unlike the PDP, a row has no breakdown to say "withheld", so
 * only a known scored status may read as "unavailable, nothing to change".
 */
function isListingScoreWithheld(params: {
  scoreMethod: ScoreMethod | null | undefined;
  listingScore: number | null | undefined;
  scoreStatus: string | null | undefined;
}): boolean {
  const { scoreMethod, listingScore, scoreStatus } = params;
  if (scoreMethod !== 'FORMULA' || listingScore != null || scoreStatus == null) return false;
  return WITHHOLDABLE_LISTING_STATUSES.has(scoreStatus);
}

export { canScoreBeWithheld, isListingScoreWithheld, resolveFormulaScoreState };
export type { FormulaComponentKey, FormulaScoreState };
